// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";

import http from "@http";
import { route } from "@routes/helper";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import { ApprovalStateEnum } from "@modules/service/constants";
import type { EapRevision } from "@modules/service/reducer/eap-signposting/types";
import type {
  ApprovableContainer,
  HierarchyAssignment,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import {
  GetEapList,
  GetEap,
  GetEapSchema,
  AddEap,
  StartDraftEap,
  DuplicateEap,
  UpdateEap,
  UpdateEapAssignments,
  DisableEap,
  DiscardEapDraft,
  SubmitEapForReview,
  CancelEapReview,
  CancelEapReviewAndDiscard,
} from "@modules/service/constants/actions";
import { parseSchema } from "@utils/schema";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

// READ

type EapListRequest = $Exact<{
  clientId: string,
}>;

export function* onGetEapList({
  payload,
}: Action<EapListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ApprovableContainer<EapRevision>>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider`,
    );

    yield put(actionCreators.GetEapListSuccess(response.data.content));
  } catch (e) {
    yield put(actionCreators.GetEapListError(e.response));
  }
}

type EapRequest = EapListRequest &
  $Exact<{
    providerId: string,
  }>;

export function* onGetEap({
  payload,
}: Action<EapRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<EapRevision>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.providerId) {
      throw Error("Invalid payload provided");
    }

    const stableResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/assignment`,
    );

    yield all([
      put(actionCreators.GetEapSuccess(stableResponse.data)),
      put(actionCreators.SetEapAssignments(assignmentsResponse.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetEapError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  providerId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    yield put(actionCreators.GetEapList({ clientId: params.clientId }));
    if (params.providerId) {
      yield put(
        actionCreators.GetEap({ clientId: params.clientId, providerId: params.providerId }),
      );
    }
  } catch (e) {
    throw e;
  }
}

// CREATE

export function* onGetEapSchema({ payload }: Action<EapRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const clientId = payload.clientId;
    const schema = yield parseSchema("EapSignpostingRevisionDTO", { clientId });
    yield put(actionCreators.GetEapSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetEapSchemaError(e.response));
  }
}

export function* onStartDraftEap({
  payload,
}: Action<EapRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<EapRevision>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.providerId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}`,
    );

    yield all([
      put(actionCreators.StartDraftEapSuccess(response.data)),
      put(actionCreators.ShowEditModal({ show: true, step: 1 })),
    ]);
  } catch (e) {
    yield put(actionCreators.StartDraftEapError(e.response));
  }
}

export function* onDuplicateEap({
  payload,
}: Action<EapRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<EapRevision>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.providerId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/active`,
    );

    yield all([
      put(actionCreators.DuplicateEapSuccess(response.data)),
      put(actionCreators.SetEapAssignments([])),
      put(actionCreators.ShowAddModal({ show: true, step: 1 })),
      put(push(route("ADMINISTRATION_SERVICE_EAP_SIGNPOSTING", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DuplicateEapError(e.response));
  }
}

type EapAddRequest = EapListRequest &
  $Exact<{
    draft: EapRevision,
    assignments: Array<HierarchyAssignment>,
  }>;

export function* onAddEap({
  payload,
}: Action<EapAddRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<EapRevision>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${response.data.id}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }

    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(actionCreators.AddEapSuccess(response.data)),
      put(actionCreators.ShowAddModal({ show: false })),
    ]);
  } catch (e) {
    yield put(actionCreators.AddEapError(e.response));
  }
}

// UPDATE

type EapUpdateRequest = EapRequest &
  $Exact<{
    draft: EapRevision,
  }>;

export function* onUpdateEap({
  payload,
}: Action<EapUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<EapRevision>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.providerId || !payload.draft) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/draft`,
      payload.draft,
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/submit-for-review`,
        {
          notes: "Automatically submitted",
        },
      );
    }

    yield call(onRefresh, { clientId: payload.clientId, providerId: payload.providerId });

    yield all([
      put(actionCreators.UpdateEapSuccess(response.data)),
      put(actionCreators.ShowEditModal({ show: false })),
      put(push(route("ADMINISTRATION_SERVICE_EAP_SIGNPOSTING", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEapError(e.response));
  }
}

type EapUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<EapRevision>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateEapAssignments({
  payload,
}: Action<EapUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error("Invalid payload provided");
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/eap/provider/${payload.container.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/eap/provider/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v1/service-admin/client/${payload.container.clientId}/service-config/absence-management/eap/provider/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      providerId: payload.container.id,
    });

    yield all([
      put(actionCreators.ShowEditAssignmentsModal(false)),
      put(
        push(
          route("ADMINISTRATION_SERVICE_EAP_SIGNPOSTING", { clientId: payload.container.clientId }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateEapAssignmentsError(e.message));
  }
}

// DISABLE

export type EapDisableRequest = EapRequest &
  $Exact<{
    approvable: ApprovableContainer<EapRevision>,
  }>;

export function* onDisable({
  payload,
}: Action<EapDisableRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<EapRevision>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.providerId || !payload.approvable) {
      throw Error("Invalid payload provided");
    }

    const draft =
      payload.approvable.approvalState === ApprovalStateEnum.ACTIVE_ONLY
        ? payload.approvable.active
        : payload.approvable.draft;

    if (!draft) {
      throw Error("Invalid draft");
    }

    const response = yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/draft`,
      {
        ...draft,
        activeFrom: draft.activeTo ? new Date().toISOString() : draft.activeFrom,
        activeTo: draft.activeTo ? null : new Date().toISOString(),
      },
    );

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/submit-for-review`,
      {
        notes: "Eap disabled and resubmitted for review",
      },
    );

    yield call(onRefresh, { clientId: payload.clientId, providerId: payload.providerId });

    yield all([
      put(actionCreators.DisableEapSuccess(response.data)),
      put(actionCreators.SetShowDisableEapModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_EAP_SIGNPOSTING", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DisableEapError(e.response));
  }
}

// STATE TRANSITIONS

export type EapTransitionRequest = $Exact<{
  clientId: string,
  providerId: string,
}>;

export function* onDiscardDraft({
  payload,
}: Action<EapTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.providerId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/draft`,
    );
    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(actionCreators.DiscardEapDraftSuccess()),
      put(actionCreators.SetShowDiscardEapModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_EAP_SIGNPOSTING", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.DiscardEapDraftError(e.response));
  }
}

export type EapSubmitForReviewRequest = $Exact<{
  ...EapTransitionRequest,
  notes: string,
}>;

export function* onSubmitForReview({
  payload,
}: Action<EapSubmitForReviewRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.providerId || !payload.notes) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/submit-for-review`,
      {
        notes: payload.notes,
      },
    );
    yield call(onRefresh, { clientId: payload.clientId, providerId: payload.providerId });

    yield all([
      put(actionCreators.SubmitEapForReviewSuccess()),
      put(actionCreators.SetShowSubmitEapModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_EAP_SIGNPOSTING", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.SubmitEapForReviewError(e.response));
  }
}

export function* onCancelReview({
  payload,
}: Action<EapTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.providerId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/cancel-review`,
    );
    yield call(onRefresh, { clientId: payload.clientId, providerId: payload.providerId });

    yield all([
      put(actionCreators.CancelEapReviewSuccess()),
      put(actionCreators.SetShowCancelEapModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_EAP_SIGNPOSTING", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelEapReviewError(e.response));
  }
}

export function* onCancelReviewAndDiscard({
  payload,
}: Action<EapTransitionRequest>): Generator<*, void, void> {
  try {
    if (!payload || !payload.clientId || !payload.providerId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/cancel-review`,
    );
    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/eap/provider/${payload.providerId}/draft`,
    );
    yield call(onRefresh, { clientId: payload.clientId });

    yield all([
      put(actionCreators.CancelEapReviewSuccess()),
      put(actionCreators.SetShowCancelDiscardEapModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_EAP_SIGNPOSTING", { clientId: payload.clientId }))),
    ]);
  } catch (e) {
    yield put(actionCreators.CancelEapReviewError(e.response));
  }
}

// ROOT

export function* EapListSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetEapList, onGetEapList),
    takeEvery(GetEap, onGetEap),
    takeEvery(GetEapSchema, onGetEapSchema),
    takeEvery(AddEap, onAddEap),
    takeEvery(StartDraftEap, onStartDraftEap),
    takeEvery(DuplicateEap, onDuplicateEap),
    takeEvery(UpdateEap, onUpdateEap),
    takeEvery(UpdateEapAssignments, onUpdateEapAssignments),
    takeEvery(DisableEap, onDisable),
    takeEvery(DiscardEapDraft, onDiscardDraft),
    takeEvery(SubmitEapForReview, onSubmitForReview),
    takeEvery(CancelEapReview, onCancelReview),
    takeEvery(CancelEapReviewAndDiscard, onCancelReviewAndDiscard),
  ]);
}

export default EapListSagas;
